import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  addCouncilLitigation,
  editComplaint,
  fetchCouncilDecisionTypes,
  fetchDecisionMeasureTypes,
} from "services/prosecutorDecipilineService";
import { Complaints, CouncilLitigation } from "types";
import { LoadingIcon } from "utils/constant";
import { COMPLIANT_STATUS } from "utils/constantValues";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  councilLitigationData: CouncilLitigation;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddCouncilLitigationDecisionModal({
  isOpen,
  setIsOpen,
  councilLitigationData,
  complaintData,
  onRefresh,
}: DecisionModalProps) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const todayMaxDate = new Date(
    Date.now() - new Date().getTimezoneOffset() * 60000,
  )
    .toISOString()
    .split("T")[0];
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    files: [] as File[],
    council_decision_summary: "",
    council_decision_day: "",
    council_decision_type: "",
    discipline_measure_type: "",
  });
  const [decisionTypes, setDecisionTypes] = useState<any>([]);
  const [decisionMeasureTypes, setDecisionMeasureTypes] = useState<any>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [stampUrl, setStampUrl] = useState<string | null>(null);
  const [stampBlob, setStampBlob] = useState<Blob | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadStamp = async () => {
      try {
        // Construct the actual API URL – adjust to your real endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL_2}organization/service-stamp/read_stamp/?admin_unit_uuid=${user.admin_unit}&service_name=pdms`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            }, // if needed
          },
        );
        if (!response.ok) throw new Error("Failed to fetch stamp");
        const blob = await response.blob(); // ✅ direct binary, no string corruption
        setStampBlob(blob);
        objectUrl = URL.createObjectURL(blob);
        setStampUrl(objectUrl);
      } catch (error) {
        console.error("Failed to load stamp", error);
      }
    };

    if (isOpen && user) loadStamp();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, user]);

  const loadDecisionTypes = useCallback(async () => {
    try {
      const resData = await fetchCouncilDecisionTypes();
      if (resData) {
        setDecisionTypes(resData);
      }
    } catch (err) {
      console.error("Failed to load decision types:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDecisionMeasureTypes = useCallback(async () => {
    try {
      const resData = await fetchDecisionMeasureTypes();
      if (resData) {
        setDecisionMeasureTypes(resData);
      }
    } catch (err) {
      console.error("Failed to load decision measure types:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDecisionTypes();
    loadDecisionMeasureTypes();
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const removeFile = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!councilLitigationData) {
      showToast("Council litigation data not found", "error");
      return;
    }
    if (!stampBlob) {
      showToast("Stamp is required.", "error");
      return;
    }

    setLoading(true);

    const data = new FormData();
    data.append("council_decision_files", formState?.files[0] || "");
    data.append("signature1", stampBlob, "stamp.png");
    data.append("council_decision_summary", formState.council_decision_summary);
    data.append("council_decision_day", formState.council_decision_day);
    data.append("council_decision_type", formState.council_decision_type);
    data.append("discipline_measure_type", formState.discipline_measure_type);
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append(
      "accused_prosecutor_name",
      complaintData?.accused_prosecutor_name || "",
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_org_id",
      complaintData?.accused_prosecutor_org_id || "",
    );
    data.append(
      "accused_prosecutor_admin_unit_id",
      complaintData?.accused_prosecutor_admin_unit_id || "",
    );
    data.append(
      "is_final_verdict_given",
      String(councilLitigationData?.is_final_verdict_given),
    );
    data.append("summon", String(councilLitigationData?.summon));

    const isFormData = true;

    try {
      await addCouncilLitigation(data, isFormData);
      await editComplaint({
        ...complaintData,
        is_final_decison_given: true,
        status: COMPLIANT_STATUS.COUNCIL_VERDICT_ISSUED,
      });

      onRefresh();
      setIsOpen(false);
      showToast("Council decision has been officially recorded", "success");
      setFormState({
        files: [] as File[],
        council_decision_summary: "",
        council_decision_day: "",
        council_decision_type: "",
        discipline_measure_type: "",
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast("Failed to record council decision", "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Final Council Decision"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Decision Details Section */}
        <section className="space-y-4">
          <div className="flex items-center">
            <div className="h-5 w-1 bg-red-700 rounded-r mr-3"></div>
            <h3 className="text-base font-semibold text-gray-800">
              Decision Details
            </h3>
          </div>

          <div className="space-y-4 pl-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decision Date <span className="text-red-600">*</span>
              </label>
              <input
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                type="date"
                name="council_decision_day"
                value={formState.council_decision_day}
                max={todayMaxDate}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decision Type <span className="text-red-600">*</span>
              </label>
              <select
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="council_decision_type"
                value={formState.council_decision_type}
                onChange={handleChange}
              >
                <option value="">Select decision type</option>
                {decisionTypes?.map((list: any) => (
                  <option key={list.id} value={list.decision_type}>
                    {list.decision_type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discipline Measure <span className="text-red-600">*</span>
              </label>
              <select
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="discipline_measure_type"
                value={formState.discipline_measure_type}
                onChange={handleChange}
              >
                <option value="">Select discipline measure</option>
                {decisionMeasureTypes?.map((list: any) => (
                  <option key={list.id} value={list.measure}>
                    {list.measure}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decision Summary <span className="text-red-600">*</span>
              </label>
              <textarea
                required
                rows={4}
                name="council_decision_summary"
                value={formState.council_decision_summary}
                onChange={handleChange}
                placeholder="Provide the official summary of the council's decision..."
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-y"
              />
            </div>
          </div>
        </section>

        {/* Decision Documents Section */}
        <section className="space-y-4">
          <div className="flex items-center">
            <div className="h-5 w-1 bg-red-700 rounded-r mr-3"></div>
            <h3 className="text-base font-semibold text-gray-800">
              Decision Documents
            </h3>
          </div>

          <div className="pl-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Official Decision Document
            </label>

            {/* File Upload Area */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-400 hover:bg-red-50 transition-colors cursor-pointer"
              onClick={triggerFileInput}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                // accept=".pdf,.doc,.docx"
                style={{ display: "none" }}
              />
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mb-3">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Upload Decision Document
                </p>
                <p className="text-xs text-gray-500">
                  PDF, DOC, DOCX files (Max 10MB)
                </p>
              </div>
            </div>

            {/* File List */}
            {formState.files.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  Selected Documents ({formState.files.length})
                </p>
                <div className="space-y-2">
                  {formState.files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2.5"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded">
                          <span className="text-xs font-medium text-red-700">
                            {file.type
                              .split("/")[1]
                              ?.substring(0, 3)
                              .toUpperCase() || "DOC"}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800 truncate max-w-xs">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {stampUrl && (
          <div className="my-4 flex items-center justify-center">
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 shadow-sm">
              <svg
                className="w-6 h-6 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-green-700 font-semibold">
                Stamp fetched successfully
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !formState.council_decision_day ||
                !formState.council_decision_type ||
                !formState.discipline_measure_type ||
                !formState.council_decision_summary
              }
              className={`flex-1 px-4 py-2.5 text-white text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 ${
                loading ||
                !formState.council_decision_day ||
                !formState.council_decision_type ||
                !formState.discipline_measure_type ||
                !formState.council_decision_summary
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-red-700 hover:bg-red-800"
              }`}
            >
              {loading ? (
                <>
                  {LoadingIcon}
                  <span>Processing...</span>
                </>
              ) : (
                "Record Council Decision"
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-3">
            This final council decision will be officially recorded in the case
            management system.
          </p>
        </div>
      </form>
    </SideModal>
  );
}
